import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Merchant } from './types';
import { MerchantType } from './store/merchantsUserSlice';

export interface MerchantFormData {
  name: string;
  username: string;
  email: string;
  phone: string;
  publicPhone: string;
  address: string;
  password?: string;
  userRole?: 'merchant_manager' | 'merchant_staff';
}

export const merchantSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .defined(),
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters')
    .defined(),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email')
    .defined(),
  phone: yup
    .string()
    .required('Personal phone number is required')
    .min(10, 'Personal phone number must be at least 10 characters')
    .defined(),
  publicPhone: yup
    .string()
    .optional()
    .default(''),
  address: yup
    .string()
    .required('Address is required')
    .min(5, 'Address must be at least 5 characters')
    .defined(),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .optional(),
  userRole: yup
    .string()
    .oneOf(['merchant_manager', 'merchant_staff'])
    .optional(),
}) as yup.ObjectSchema<MerchantFormData>;

export const merchantCreateSchema = merchantSchema.shape({
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .defined(),
}) as yup.ObjectSchema<MerchantFormData & { password: string }>;

export const getMerchantFormConfig = (initialMerchant: Merchant | null, isEditMode: boolean) => {
  const schema = isEditMode ? merchantSchema : merchantCreateSchema;

  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
    defaultValues: initialMerchant ? {
      name: initialMerchant.name || '',
      username: initialMerchant.username || '',
      email: initialMerchant.email || '',
      phone: initialMerchant.phone || '',
      publicPhone: initialMerchant.publicPhone || '',
      address: initialMerchant.address || '',
      userRole: initialMerchant.role as any || undefined,
    } : {
      name: '',
      username: '',
      email: '',
      phone: '',
      publicPhone: '',
      address: '',
      password: '',
      userRole: undefined,
    },
  };
};

export const getMerchantType = (isManagerMode: boolean, isStaffMode: boolean): MerchantType => {
  if (isManagerMode) return 'merchant_manager';
  if (isStaffMode) return 'merchant_staff';
  return 'merchant';
};

export const getMerchantLabel = (isManagerMode: boolean, isStaffMode: boolean): string => {
  if (isManagerMode) return 'Merchant Manager';
  if (isStaffMode) return 'Staff';
  return 'Merchant';
};

export type MerchantUserRoleType = 'merchant_manager' | 'merchant_staff';

export const MERCHANT_USER_ROLE_OPTIONS: { value: MerchantUserRoleType; label: string }[] = [
  { value: 'merchant_manager', label: 'Manager' },
  { value: 'merchant_staff', label: 'Staff' },
];

export const getMerchantUserRoleLabel = (role: MerchantUserRoleType | string): string => {
  if (role === 'merchant_manager') return 'Manager';
  if (role === 'merchant_staff') return 'Staff';
  return role || '';
};
